import { Icon } from '@iconify/react';

// A tenant is Active until their end date (the last day of the tenancy) has
// passed. The server sends tenancy_status and is_leaving on every tenant row.

// DD/MM/YYYY for every member list. A date-only value ("2026-09-22") is read
// as written - new Date() would treat it as UTC midnight and could show the
// previous day west of UTC.
export const formatDate = (value) => {
    if (!value) return '-';
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

// Name shown for a member: a company owner (entity type C) has no first/last
// name, so its company name is used - the list showed a blank otherwise.
export const memberDisplayName = (member = {}) => {
    const personal = [member.first_name, member.last_name].filter(Boolean).join(' ');
    if (member.entity_type === 'C' || !personal) return member.company_name || personal || '-';
    return personal;
};

export const memberInitials = (member = {}) => {
    const words = memberDisplayName(member).split(/\s+/).filter(w => /[A-Za-z0-9]/.test(w));
    const initials = words.length > 1 ? words[0][0] + words[words.length - 1][0] : (words[0] || '').slice(0, 2);
    return initials.toUpperCase() || '?';
};

export const TenancyStatusBadge = ({ member }) => {
    if (!member?.tenancy_status) return null;
    if (member.tenancy_status === 'INACTIVE') {
        return (
            <span className="tw-text-[10px] tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-gray-100 tw-text-gray-600 tw-whitespace-nowrap">
                Inactive
            </span>
        );
    }
    if (member.is_leaving) {
        return (
            <span
                className="tw-text-[10px] tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-amber-100 tw-text-amber-700 tw-whitespace-nowrap"
                title="Active until the end date, inactive from the next day"
            >
                Leaving {formatDate(member.end_date)}
            </span>
        );
    }
    return (
        <span className="tw-text-[10px] tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-emerald-100 tw-text-emerald-700 tw-whitespace-nowrap">
            Active
        </span>
    );
};

// Action buttons shown next to View / Edit / Delete for tenant rows.
export const TenancyActions = ({ member, onToggleStatus, onHistory, buttonClass = 'tw-p-1.5 tw-rounded' }) => {
    if (!member?.tenancy_status) return null;
    const ended = member.tenancy_status === 'INACTIVE';
    return (
        <>
            {onToggleStatus && (
                <button
                    onClick={() => onToggleStatus(member)}
                    className={`${buttonClass} ${ended
                        ? 'tw-text-gray-400 hover:tw-bg-emerald-50 hover:tw-text-emerald-600'
                        : 'tw-text-gray-400 hover:tw-bg-amber-50 hover:tw-text-amber-600'}`}
                    title={ended ? 'Mark Active' : 'Mark Inactive'}
                >
                    <Icon icon={ended ? 'mdi:account-check-outline' : 'mdi:account-off-outline'} className="tw-text-base" />
                </button>
            )}
            {onHistory && (
                <button
                    onClick={() => onHistory(member)}
                    className={`${buttonClass} tw-text-gray-400 hover:tw-bg-indigo-50 hover:tw-text-indigo-600`}
                    title="Tenant history of this flat"
                >
                    <Icon icon="mdi:history" className="tw-text-base" />
                </button>
            )}
        </>
    );
};

// Tenant-facing document types (Police NOC and rental contract). `OTHER` is a
// valid backend value but we don't expose it on this form — the slots here
// mirror what field staff collect at onboarding.
export const TENANT_DOCUMENT_SLOTS = [
    { key: 'POLICE_NOC', label: 'Police NOC', icon: 'mdi:shield-check-outline' },
    { key: 'AGREEMENT',  label: 'Rental Agreement / Leave & License', icon: 'mdi:file-document-outline' }
];

// The BE returns document paths as origin-relative `/uploads/...` strings.
// Browsers would resolve those against the FE origin (404s), so we prefix
// with the API origin. Absolute URLs from the BE (S3/GCS in the future) pass
// through unchanged.
const API_ORIGIN = (process.env.REACT_APP_NODE_MOBILE_BASE_URL || '').replace(/\/+$/, '');
export const absoluteFileUrl = (p) => {
    if (!p) return '#';
    if (/^https?:\/\//i.test(p)) return p;
    return API_ORIGIN + (p.startsWith('/') ? p : '/' + p);
};
